import { useSearchParams } from 'react-router-dom'
import { OpsOrderDetail } from './OpsOrderDetail'
import { useMemo, useRef, useState } from 'react'
import { MinimalButton, MinimalDropdown } from '@ovasabi/ui-minimal'
import { confirmedUpsert } from '../choosechow/chefShared'
import { commandPayload, commands } from '../../lib/runtime'
import * as S from './OpsStyle'
import { formatNaira, formatWhen, useOpsOrders, useProfileNames, type OrderRow } from './opsData'

/**
 * Order desk. What a customer-relations agent actually does with an order:
 * find it, see where it is stuck, and either move it or refund it.
 *
 * The transitions offered here are the ones the ORDER LIFECYCLE permits from
 * the order's current status (internal/service/marketplace/order_lifecycle.go),
 * for a privileged actor. The console does not offer a button the server will
 * refuse — and if the server refuses anyway, the reason is shown rather than
 * swallowed, because "nothing happened" is the failure mode this whole pass has
 * been about.
 */

/** Mirrors the server's transition table for a privileged actor. */
const OPERATOR_TRANSITIONS: Record<string, readonly string[]> = {
  draft: ['cancelled'],
  pending_payment: ['cancelled'],
  paid: ['accepted', 'cancelled'],
  accepted: ['preparing', 'cancelled'],
  preparing: ['ready', 'cancelled'],
  ready: ['delivery_requested', 'completed', 'cancelled'],
  delivery_requested: ['in_delivery', 'cancelled'],
  in_delivery: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
  refunded: [],
}

const TONE: Record<string, 'ok' | 'warn' | 'bad' | 'flat'> = {
  completed: 'ok',
  paid: 'ok',
  cancelled: 'bad',
  refunded: 'bad',
  pending_payment: 'warn',
  draft: 'flat',
}

export const OpsOrdersPage = () => {
  const [params, setParams] = useSearchParams()
  const statusFilter = params.get('status') ?? ''
  const orderId = params.get('order') ?? ''
  const orders = useOpsOrders()
  const names = useProfileNames()
  const [query, setQuery] = useState('')
  const [busyId, setBusyId] = useState('')
  const [error, setError] = useState('')
  const pending = useRef(false)
  const [selection, setSelection] = useState<{ id: string; status: string } | null>(null)

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return orders.filter((o) => {
      if (statusFilter === 'in_flight' && !['paid', 'accepted', 'preparing', 'ready', 'delivery_requested', 'in_delivery'].includes(o.status)) return false
      if (statusFilter && statusFilter !== 'in_flight' && statusFilter !== o.status) return false
      if (!needle) return true
      const customer = names.get(o.customerProfileId) ?? ''
      const merchant = names.get(o.merchantProfileId) ?? ''
      return (
        o.reference.toLowerCase().includes(needle) ||
        o.id.toLowerCase().includes(needle) ||
        o.status.includes(needle) ||
        customer.toLowerCase().includes(needle) ||
        merchant.toLowerCase().includes(needle)
      )
    })
  }, [orders, names, query, statusFilter])

  const move = async (order: OrderRow, next: string) => {
    if (pending.current) return
    pending.current = true
    setBusyId(order.id)
    setError('')
    try {
      const updated = commandPayload(await commands.marketplace.setOrderStatus(order.id, {
        status: next,
        reason: 'console:operator_move',
      }))
      if (String(updated.id ?? '') !== order.id || !updated.status) throw new Error('The order update was not confirmed.')
      confirmedUpsert('marketplace.marketplace_orders', updated)
      setSelection(null)
    } catch (err) {
      // The server names the refusal (which transitions were available); show it.
      setError(err instanceof Error ? err.message : 'The order could not be moved.')
    } finally {
      pending.current = false
      setBusyId('')
    }
  }

  return (
    <>
      <div>
        <S.H1>Orders</S.H1>
      </div>
      {statusFilter && <S.Action onClick={() => setParams({})}>Showing {statusFilter.replace(/_/g, ' ')} · Show all orders</S.Action>}
      {orderId && <OpsOrderDetail orderId={orderId} onClose={() => { const next = new URLSearchParams(params); next.delete('order'); setParams(next) }} />}
      <S.Card>
        <S.Field>
          Search by reference, id, status, customer, or kitchen
          <S.Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. preparing, Amara, 7f3c…"
          />
        </S.Field>
        {error && <S.ErrorNote role="alert">{error}</S.ErrorNote>}
      </S.Card>
      <S.Card>
        <S.CardTitle>
          {visible.length} order{visible.length === 1 ? '' : 's'}
        </S.CardTitle>
        {visible.length === 0 ? (
          <S.Empty>No orders match.</S.Empty>
        ) : (
          <S.TableScroll>
            <S.Table>
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Kitchen</th>
                  <th>Total</th>
                  <th>Placed</th>
                  <th>Move to</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((order) => {
                  const status = order.status
                  const targets = OPERATOR_TRANSITIONS[status] ?? []
                  return (
                    <tr key={order.id}>
                      <td><S.Action onClick={() => { const next = new URLSearchParams(params); next.set('order', order.id); setParams(next) }}>{order.reference}</S.Action></td>
                      <td>
                        <S.Pill $tone={TONE[status] ?? 'flat'}>{status.replace(/_/g, ' ')}</S.Pill>
                      </td>
                      <td>{names.get(order.customerProfileId) ?? '—'}</td>
                      <td>{names.get(order.merchantProfileId) ?? '—'}</td>
                      <td>{formatNaira(order.totalMinor)}</td>
                      <td>{formatWhen(order.createdAt)}</td>
                      <td>
                        {targets.length === 0 ? (
                          <S.Pill $tone="flat">final</S.Pill>
                        ) : (
                          <div>
                            <MinimalDropdown
                              label={`Next step · ${order.reference}`}
                              disabled={Boolean(busyId)}
                              value={selection?.id === order.id ? selection.status : undefined}
                              placeholder="Choose next step"
                              options={targets.map((target) => ({ value: target, label: target.replace(/_/g, ' ') }))}
                              onChange={(status) => setSelection({ id: order.id, status })}
                            />
                            {selection?.id === order.id && targets.includes(selection.status) && (
                              <MinimalButton size="sm" disabled={Boolean(busyId)} onClick={() => void move(order, selection.status)}>
                                {busyId === order.id ? 'Updating…' : `Confirm ${selection.status.replace(/_/g, ' ')}`}
                              </MinimalButton>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </S.Table>
          </S.TableScroll>
        )}
        <S.Note>
          Payment and refund status must come from billing. Changing an order label does not capture or return money.
        </S.Note>
      </S.Card>
    </>
  )
}
